import React, { type ComponentType, lazy, Suspense, type ComponentProps } from 'react';

import Loadable from 'react-loadable';

import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { Dialogues } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';

import type { ConversationActionWithAssistantMetadata } from '../active-action/action-capabilities';
import { TAGS } from '../chat-store/types';

import { INSIGHT_ACTION_ID_PREFIX, type OpenInsight } from './insight-action';

const InsightChatViewLazyComponent = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-insight-chat-view" */
		'../../ui/chat-history/insight-chat-view/InsightChatView'
	).then((m) => ({ default: m.InsightChatView })),
);
Object.assign(InsightChatViewLazyComponent, { displayName: 'lazy(InsightChatView)' });

const InsightChatViewLoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlassian/conversation-assistant-insight-chat-view" */
			'../../ui/chat-history/insight-chat-view/InsightChatView'
		).then((m) => m.InsightChatView),
	loading: () => null,
});

function InsightChatViewWithSuspense(
	props: ComponentProps<typeof InsightChatViewLazyComponent>,
): React.JSX.Element {
	return (
		<Suspense fallback={null}>
			<InsightChatViewLazyComponent {...props} />
		</Suspense>
	);
}

const InsightChatView: React.FC<
	ComponentProps<typeof InsightChatViewLazyComponent> & React.RefAttributes<unknown>
> = (props) =>
	fg('nike_r19_react_loadable') ? (
		<InsightChatViewWithSuspense {...props} />
	) : (
		<InsightChatViewLoadableComponent {...props} />
	);

export const insightToConversationAction = (
	insight: OpenInsight,
	icon: ComponentType<NewCoreIconProps>,
	lifecycle: {
		onDismiss: () => void;
		onCreated: (conversationId: string) => void;
	},
): ConversationActionWithAssistantMetadata => {
	const InsightIcon = icon;
	const { agentMessageAdf, title, followUps } = insight;

	return {
		id: `${INSIGHT_ACTION_ID_PREFIX}:${crypto.randomUUID()}`,
		title,
		followUps,
		presentation: {
			hideActionHeader: true,
			suppressPromptLibrary: true,
			centerInFullScreen: true,
		},
		seededConversation: {
			name: title,
			followUpSource: 'insightsFeed',
			tags: [TAGS.INSIGHTS],
			skipNextFetch: true,
			onCreated: lifecycle.onCreated,
		},
		onDismiss: lifecycle.onDismiss,
		handleType: HandleActionType.Base,
		icon: () => <InsightIcon label={title} />,
		viewConfig: {
			render: () => <InsightChatView agentMessageAdf={agentMessageAdf} />,
		},
		customFollowUpContext: {
			available: true,
			fetch: async (): Promise<Dialogues[]> => [
				{
					human_message: { content: '' },
					agent_message: { content: agentMessageAdf, mimeType: 'text/adf' },
				},
			],
		},
	};
};
